import React, { FC, useMemo, useCallback } from "react";
import { EmptyStateComponent } from "../EmptyStateComponent";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import { CircleAlert, FileDown } from "lucide-react";
import { Skeleton, Card, CardHeader, CardContent } from "@/components/ui";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/Chart";
import { StudentsBarChartProps } from "./types";
import { ChartHeader } from "@/components/ChartHeader";
import { FUNNEL_CHART_DATA } from "@/api/queries/funnelChartData";
import { useQuery } from "@apollo/client";
import { useOrg } from "@/hooks/useOrg";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { FilterState } from "../FilterSidebar/FilterState";
import { getGroupNames, convertFunnelStatesGroups } from "@/util/funnelGroups";
import { useTranslation } from "react-i18next";
import { Legend } from "./Legend";
import { processChartData, ProcessedChartData } from "./utils";
import { TooltipLineItem } from "@/components/ChartTooltip";
import { useProtectedNavToStudent } from "@/routes/buildStudentsListLink";
import { YesNoEnum } from "@/__generated__/types";
import { toast } from "sonner";
import { exportChartCSV } from "@/util/exportChartCSV";

const StudentsBarChart: FC<StudentsBarChartProps> = ({
  title,
  description,
  chartConfig,
  xAxisKey,
  menuOptions,
}) => {
  const { org, loading: loadingOrg } = useOrg();
  const { cohortYear, loading: loadingCohort } = useCurrentCohortYear();
  const today = useMemo(() => new Date(), []);
  const { t } = useTranslation();
  const navToStudent = useProtectedNavToStudent();

  const { data, loading, error } = useQuery(FUNNEL_CHART_DATA, {
    variables: {
      orgId: org?.id || "",
      date: today.toISOString().slice(0, 10),
      filters: new FilterState({
        cohortYear: cohortYear ? [cohortYear.toString()] : [],
      }).getQueryFilter(),
    },
    skip: !org?.id || !cohortYear,
  });

  const chartColumns = getGroupNames("COMMUNITY_ADOPTION_CHART");
  const percentData = processChartData(data);

  const handleExportCSV = useCallback(() => {
    if (!percentData || percentData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    try {
      const headers = [
        "",
        t("adoption.chartAdoption.csvHeaders.inCommunityPercent"),
        t("adoption.chartAdoption.csvHeaders.notInCommunityPercent"),
        t("adoption.chartAdoption.csvHeaders.inCommunityCount"),
        t("adoption.chartAdoption.csvHeaders.notInCommunityCount"),
      ];

      const exportData = percentData.map((dataPoint) => {
        const dataWithCounts = dataPoint as ProcessedChartData & {
          inCommunityCount: number;
          notInCommunityCount: number;
        };

        return {
          [headers[0]]: t(`funnelGroups.${dataPoint.groupName}`),
          [headers[1]]: `${dataPoint.inCommunity}%`,
          [headers[2]]: `${dataPoint.notInCommunity}%`,
          [headers[3]]: dataWithCounts.inCommunityCount,
          [headers[4]]: dataWithCounts.notInCommunityCount,
        };
      });

      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}`;

      exportChartCSV({
        filename: `community-adoption-by-funnel-state-${timestamp}`,
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [percentData, t]);

  const studentBarChartMenu = useMemo(() => {
    const exportOption = {
      label: t("global.exportToCSV.menuTitle"),
      icon: <FileDown aria-hidden height={16} />,
      onClick: handleExportCSV,
    };

    if (!menuOptions) {
      return [exportOption];
    }

    return [exportOption, ...menuOptions];
  }, [t, handleExportCSV, menuOptions]);

  if (loading || loadingOrg || loadingCohort) {
    return (
      <Card className="h-full w-full" data-testid="recharts-card-wrapper">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-8 w-8" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col pb-10">
          <div className="mb-10 flex flex-col-reverse items-start justify-between gap-2 sm:flex-row sm:items-center">
            <div className="mb-4 flex gap-2"></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Skeleton className="h-2 w-5" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center gap-1">
                <Skeleton className="h-2 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
          <div className="flex gap-4 pr-5 pl-10">
            {chartColumns.map((col) => {
              return <Skeleton key={col} className="h-103 w-10 grow" />;
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full w-full" data-testid="recharts-card-wrapper">
        <ChartHeader
          {...{
            title,
            subtitle: description ?? "",
            menuOptions: studentBarChartMenu,
          }}
        />
        <CardContent className="flex flex-1 items-center justify-center">
          <EmptyStateComponent
            message={t("predictiveModels.chart.error.loadingError")}
            icon={<CircleAlert className="size-12" />}
          />
        </CardContent>
      </Card>
    );
  }

  const handleBarClick = (data: ProcessedChartData) => {
    const groupName = data.groupName;
    const filterSidebarFunnelGroups = convertFunnelStatesGroups(
      [groupName],
      "COMMUNITY_ADOPTION_CHART",
      "SIDEBAR",
    );

    navToStudent(
      {
        fs: new FilterState({
          funnelStates: filterSidebarFunnelGroups,
          inCommunity: YesNoEnum.Yes,
        }),
      },
      "Community Snapshot - Students in Your Community",
    );
  };

  return (
    <Card className="h-full w-full" data-testid="recharts-card-wrapper">
      <ChartHeader
        {...{
          title,
          subtitle: description ?? "",
          menuOptions: studentBarChartMenu,
        }}
      />
      <CardContent className="flex flex-col gap-2">
        <div className="flex items-end justify-end gap-4 text-sm">
          {Object.entries(chartConfig).map(([key, { label, color }]) => (
            <div key={key} className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{
                  backgroundColor: color,
                  width: "20px",
                  height: "5px",
                }}
              />
              <span className="text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
        <Legend chartConfig={chartConfig} />
        <ChartContainer
          config={chartConfig}
          className="relative mt-4 h-[451px] w-full"
        >
          <BarChart
            accessibilityLayer
            data={percentData}
            data-testid="recharts-wrapper"
          >
            <CartesianGrid horizontal />
            <XAxis
              dataKey={xAxisKey}
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(tick) => `${tick}%`}
              axisLine={false}
              tickLine={false}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  reversePayload={true}
                  formatter={(value, nameType, item) => {
                    const customValue =
                      nameType === "inCommunity"
                        ? item.payload.inCommunityCount
                        : item.payload.notInCommunityCount;
                    return (
                      <TooltipLineItem
                        label={`${chartConfig[nameType].label}`}
                        value={customValue.toLocaleString()}
                        color={chartConfig[nameType].color || ""}
                      />
                    );
                  }}
                  className="w-[250px]"
                />
              }
            />
            {Object.keys(chartConfig)
              .reverse()
              .map((key, index, array) => {
                const isTop = index === array.length - 1;
                const isInCommunity = key === "inCommunity";

                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a"
                    onClick={isInCommunity ? handleBarClick : undefined}
                    className={isInCommunity ? "hover:cursor-pointer" : ""}
                    fill={chartConfig[key].color}
                    radius={isTop ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  >
                    <LabelList
                      dataKey={key}
                      position="center"
                      formatter={(value: number) =>
                        value > 0 ? `${value}%` : ""
                      }
                      style={{
                        fill: "white",
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    />
                  </Bar>
                );
              })}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export { StudentsBarChart };
